import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Accordion, InputField } from '@ets/design-system';

import { MeasureInput } from '_common/components';
import { MeasureInputProps } from '_common/components/MeasureInput/MeasureInput';

import { convertToDataUnit, convertToDisplayUnit } from '../utils';

import styles from './Section.module.scss';

type PaddingProps = Pick<Editor.Edition.TableProperties, 'padding'> & SectionProps;

/**
 * Received values unit is pt
 * Value should be displayed in cm, so must convert to cm
 * When updating data convert value back to point
 */

const Padding = ({ padding, updateData }: PaddingProps) => {
  const intl = useIntl();

  const [localTop, setLocalTop] = useState(convertToDisplayUnit(padding?.top?.value));
  const [localRight, setLocalRight] = useState(convertToDisplayUnit(padding?.right?.value));
  const [localBottom, setLocalBottom] = useState(convertToDisplayUnit(padding?.bottom?.value));
  const [localLeft, setLocalLeft] = useState(convertToDisplayUnit(padding?.left?.value));

  const handleTopChange: MeasureInputProps['onChange'] = (newValue) => {
    setLocalTop(+newValue);
    updateData({
      property: 'padding',
      value: { ...padding, top: { value: convertToDataUnit(+newValue) } },
    });
  };
  const handleRightChange: MeasureInputProps['onChange'] = (newValue) => {
    setLocalRight(+newValue);
    updateData({
      property: 'padding',
      value: { ...padding, right: { value: convertToDataUnit(+newValue) } },
    });
  };
  const handleBottomChange: MeasureInputProps['onChange'] = (newValue) => {
    setLocalBottom(+newValue);
    updateData({
      property: 'padding',
      value: { ...padding, bottom: { value: convertToDataUnit(+newValue) } },
    });
  };
  const handleLeftChange: MeasureInputProps['onChange'] = (newValue) => {
    setLocalLeft(+newValue);
    updateData({
      property: 'padding',
      value: { ...padding, left: { value: convertToDataUnit(+newValue) } },
    });
  };

  return (
    <Accordion
      size="m"
      variant="standard"
      title={intl.formatMessage({ id: 'PADDING' })}
      defaultOpen={true}
      testId="tableProperties-padding"
    >
      <div className={styles.root}>
        <div className={styles.column}>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'TOP' })}
            testId="tableProperties-padding-top"
          >
            <div className={styles.row}>
              <MeasureInput
                size="m"
                value={`${localTop}`}
                onChange={handleTopChange}
                placeholder=""
                valueSuffix="cm"
                avoidEmpty
                min={0}
                testId="tableProperties-padding-top"
              />
            </div>
          </InputField>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'LEFT' })}
            testId="tableProperties-padding-left"
          >
            <div className={styles.row}>
              <MeasureInput
                size="m"
                value={`${localLeft}`}
                onChange={handleLeftChange}
                placeholder=""
                valueSuffix="cm"
                avoidEmpty
                min={0}
                testId="tableProperties-padding-left"
              />
            </div>
          </InputField>
        </div>
        <div className={styles.column}>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'BOTTOM' })}
            testId="tableProperties-padding-bottom"
          >
            <div className={styles.row}>
              <MeasureInput
                size="m"
                value={`${localBottom}`}
                onChange={handleBottomChange}
                placeholder=""
                valueSuffix="cm"
                avoidEmpty
                min={0}
                testId="tableProperties-padding-bottom"
              />
            </div>
          </InputField>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'RIGHT' })}
            testId="tableProperties-padding-right"
          >
            <div className={styles.row}>
              <MeasureInput
                size="m"
                value={`${localRight}`}
                onChange={handleRightChange}
                placeholder=""
                valueSuffix="cm"
                avoidEmpty
                min={0}
                testId="tableProperties-padding-right"
              />
            </div>
          </InputField>
        </div>
      </div>
    </Accordion>
  );
};

export default Padding;
